"use client";

import { useKalshiLiveData } from "@/hooks/useKalshiLiveData";

interface Props {
  eventTicker: string | null | undefined;
  className?: string;
}

/**
 * Compact live score badge for Kalshi sports events. Renders nothing when
 * the event has no associated milestone or no live data available.
 */
export default function KalshiLiveScoreBadge({ eventTicker, className = "" }: Props) {
  const { data } = useKalshiLiveData(eventTicker, !!eventTicker);
  if (!data) return null;

  const isLive =
    data.widgetStatus === "in_progress" ||
    data.status === "inprogress" ||
    data.status === "open";
  const isFinished =
    data.widgetStatus === "finished" || data.status === "closed";

  // Only render a score when Kalshi actually gave us one. Tournament-style
  // sports (golf, big-field events) don't have a home/away concept, so
  // falling back to 0 produced a fake "0-0" next to LIVE.
  const hasScore = data.homePoints != null || data.awayPoints != null;
  const home = data.homePoints ?? 0;
  const away = data.awayPoints ?? 0;
  const period = data.periodLabel || (data.period ? `P${data.period}` : "");

  return (
    <div
      className={`inline-flex items-center gap-2 px-2.5 py-1 rounded-[5px] text-[11px] font-bold font-inter-tight ${
        isLive
          ? "bg-red-500/15 text-red-600 dark:text-red-400 border border-red-500/30"
          : isFinished
          ? "bg-gray-100 dark:bg-gray-800 text-gray-600 dark:text-gray-400"
          : "bg-blue-500/10 text-blue-600 dark:text-blue-400 border border-blue-500/20"
      } ${className}`}
    >
      {isLive && <span className="w-1.5 h-1.5 rounded-[5px] bg-red-500 animate-pulse" />}
      <span>
        {isLive ? "LIVE" : isFinished ? "FINAL" : "SCHEDULED"}
      </span>
      {(isLive || isFinished) && hasScore && (
        <span className="tabular-nums">
          {away}-{home}
        </span>
      )}
      {isLive && period && <span className="opacity-70">{period}</span>}
      {isLive && data.clock && <span className="opacity-70 tabular-nums">{data.clock}</span>}
    </div>
  );
}
